import React from "react";
import { MessageSquare } from "lucide-react";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonList } from "@/components/ui/skeleton";
import { StatusIndicator } from "@/components/ui/status-indicator";
import { Conversation, colors } from "@/types/inbox.types";
import { format } from "date-fns";

interface ConversationListPanelProps {
  isLoading: boolean;
  conversations: Conversation[];
  selectedConversationId: string | null;
  searchTerm: string;
  onConversationClick: (conversationId: string) => void;
}

export const ConversationListPanel = ({
  isLoading,
  conversations,
  selectedConversationId,
  searchTerm,
  onConversationClick,
}: ConversationListPanelProps) => {
  return (
    <div
      className={`${
        selectedConversationId ? "hidden md:flex md:w-1/4" : "w-full md:w-1/4"
      } border-r flex-col bg-white`}
    >
      <div className="flex-1 overflow-auto min-h-0">
        {isLoading ? (
          <div className="p-4">
            <SkeletonList
              items={6}
              showAvatar={false}
              avatarSize="md"
              showDescription={true}
              showBadge={true}
              spacing="normal"
            />
          </div>
        ) : conversations.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={MessageSquare}
              title={
                searchTerm
                  ? "No matching conversations"
                  : "No conversations yet"
              }
              description={
                searchTerm
                  ? "Try a different search term"
                  : "When users chat with your agents, conversations will appear here."
              }
              iconClassName="text-indigo-400"
            />
          </div>
        ) : (
          <div className="flex flex-col h-full">
            {/* Conversation list */}
            <div className="flex-1 overflow-auto divide-y divide-slate-200">
              {conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className={`p-4 ${colors.conversationHover} cursor-pointer transition-colors ${
                    selectedConversationId === conversation.id
                      ? colors.selectedConversation
                      : ""
                  }`}
                  onClick={() => onConversationClick(conversation.id)}
                >
                  <div className="flex items-center gap-3">
                    <StatusIndicator status={conversation.status} size="md" />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="text-[14px] font-semibold truncate text-slate-800">
                            {conversation.user_name ||
                              conversation.user_id ||
                              "Anonymous User"}
                          </span>
                          {/* Priority dot for high/urgent conversations */}
                          {(conversation.priority === "high" ||
                            conversation.priority === "urgent") && (
                            <div
                              className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                conversation.priority === "urgent"
                                  ? "bg-red-500"
                                  : "bg-orange-500"
                              }`}
                            />
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-slate-500 whitespace-nowrap">
                            {format(
                              new Date(conversation.created_at),
                              "MMM d, h:mm a",
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pl-11">
                    {/* Show conversation intent or fallback message */}
                    <div className="text-[12px] text-gray-500 line-clamp-2 mb-1">
                      {conversation.intent || "No conversation intent"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
